/**
 * Historial de conversaciones (2026-10-26, pedido de Osvaldo Soruco, Hipermaxi: "consultar el historial").
 *
 * Una fila por caso (lo que el personal habló con el asistente para un cliente), la más reciente primero;
 * al abrirla, los mensajes en orden con la respuesta, el producto, si derivó y qué votaron. Filtra por
 * sucursal y persona, y se baja en CSV (Excel). Lee GET /api/uso/historial con el token personal.
 */
import { useEffect, useState } from 'react'
import { API_BASE } from '../api/client'

interface Mensaje {
  ts: string
  perfil: string | null
  mensaje: string
  respuesta: string | null
  producto: string | null
  derivacion: string | null
  voto: string | null
}
interface Caso {
  inicio: string
  fin: string
  usuario: string
  nombre: string
  sucursal: string | null
  mensajes: Mensaje[]
}

const PERFIL: Record<string, string> = {
  adult: 'Adulto', child_3y: 'Niño 0–4', child_5y: 'Niño 5–7', child_8y: 'Niño 8–11', child_12y: 'Niño 12+',
  pregnancy: 'Embarazo', lactation: 'Lactancia',
}
const VOTO: Record<string, string> = {
  up: 'le sirvió', down: 'no le sirvió', vendido: 'vendido', otro_producto: 'vendió otro', no_vendido: 'no se vendió',
}
const DERIVA: Record<string, string> = { urgencias: 'derivó a emergencias', medico_hoy: 'derivó: médico hoy',
  medico_pronto: 'derivó: médico pronto' }

const token = () => ({ 'X-Device-Token': localStorage.getItem('device_token') ?? '' })
const hora = (ts: string) =>
  new Date(ts).toLocaleString('es-BO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
const sucursalCorta = (s: string | null) => (s ?? '').replace(/^FARMACIA\s+/i, '')
// la cuenta de la sucursal ya se llama "Farmacia Centenario": no se repite "· CENTENARIO"
const quien = (c: Caso) => {
  const nombre = c.nombre || c.usuario
  const suc = sucursalCorta(c.sucursal)
  return suc && !nombre.toUpperCase().includes(suc.toUpperCase()) ? `${nombre} · ${suc}` : nombre
}

interface Props {
  dias: number
  sucursales: string[]
  personas: { email: string; nombre: string }[]
}

export function Historial({ dias, sucursales, personas }: Props) {
  const [sucursal, setSucursal] = useState('')
  const [usuario, setUsuario] = useState('')
  const [casos, setCasos] = useState<Caso[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [bajando, setBajando] = useState(false)

  const query = () => {
    const q = new URLSearchParams({ dias: String(dias) })
    if (sucursal) q.set('sucursal', sucursal)
    if (usuario) q.set('usuario', usuario)
    return q
  }

  useEffect(() => {
    let vivo = true
    setCasos(null)
    setError(null)
    fetch(`${API_BASE}/uso/historial?${query()}`, { headers: token() })
      .then(async (r) => {
        if (!r.ok) throw new Error(`Error ${r.status}`)
        return r.json()
      })
      .then((d) => vivo && setCasos(d.casos))
      .catch((e) => vivo && setError(String(e.message || e)))
    return () => {
      vivo = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dias, sucursal, usuario])

  const bajar = async () => {
    setBajando(true)
    try {
      const q = query()
      q.set('formato', 'csv')
      const r = await fetch(`${API_BASE}/uso/historial?${q}`, { headers: token() })
      if (!r.ok) throw new Error(`Error ${r.status}`)
      const url = URL.createObjectURL(await r.blob())
      const a = document.createElement('a')
      a.href = url
      a.download = /filename=([^;]+)/.exec(r.headers.get('Content-Disposition') ?? '')?.[1] ?? 'historial.csv'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(String((e as Error).message || e))
    } finally {
      setBajando(false)
    }
  }

  return (
    <section className="historial" aria-label="Historial de conversaciones">
      <div className="historial-cabeza">
        <h2 className="historial-titulo">Historial de conversaciones</h2>
        <div className="historial-filtros">
          <label>
            <span className="sr-only">Sucursal</span>
            <select value={sucursal} onChange={(e) => setSucursal(e.target.value)} aria-label="Sucursal">
              <option value="">Todas las sucursales</option>
              {sucursales.map((s) => <option key={s} value={s}>{sucursalCorta(s)}</option>)}
            </select>
          </label>
          <label>
            <span className="sr-only">Persona</span>
            <select value={usuario} onChange={(e) => setUsuario(e.target.value)} aria-label="Persona">
              <option value="">Todas las personas</option>
              {personas.map((p) => <option key={p.email} value={p.email}>{p.nombre || p.email}</option>)}
            </select>
          </label>
          <button type="button" className="historial-bajar" onClick={bajar} disabled={bajando}>
            {bajando ? 'Bajando…' : 'Bajar en Excel (CSV)'}
          </button>
        </div>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!casos && !error && <p className="historial-vacio">Cargando…</p>}
      {casos && casos.length === 0 && <p className="historial-vacio">No hay conversaciones en este período.</p>}
      {casos && casos.length > 0 && (
        <ul className="historial-lista">
          {casos.map((c) => {
            const deriva = c.mensajes.some((m) => m.derivacion)
            const voto = c.mensajes.map((m) => m.voto).find(Boolean)
            return (
              <li key={`${c.usuario}-${c.inicio}`}>
                <details className="caso">
                  <summary>
                    <span className="caso-fecha">{hora(c.inicio)}</span>
                    <span className="caso-quien">{quien(c)}</span>
                    <span className="caso-primero">«{c.mensajes[0]?.mensaje}»</span>
                    <span className="caso-marcas">
                      {c.mensajes.length} {c.mensajes.length === 1 ? 'mensaje' : 'mensajes'}
                      {deriva && <span className="caso-marca deriva">derivó</span>}
                      {voto && <span className="caso-marca">{VOTO[voto] ?? voto}</span>}
                    </span>
                  </summary>
                  <ol className="caso-mensajes">
                    {c.mensajes.map((m, i) => (
                      <li key={i}>
                        <div className="msg-personal">
                          <span className="msg-quien">Personal · {hora(m.ts)}{m.perfil ? ` · ${PERFIL[m.perfil] ?? m.perfil}` : ''}</span>
                          {m.mensaje}
                        </div>
                        <div className="msg-asistente">
                          <span className="msg-quien">Asistente</span>
                          {m.respuesta || '—'}
                          {(m.producto || m.derivacion || m.voto) && (
                            <span className="msg-datos">
                              {m.producto && <span>Producto: {m.producto}</span>}
                              {m.derivacion && <span className="deriva">{DERIVA[m.derivacion] ?? `derivó (${m.derivacion})`}</span>}
                              {m.voto && <span>Voto: {VOTO[m.voto] ?? m.voto}</span>}
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                </details>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
